import { Button, EmptyState, Skeleton, Spinner } from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { CadenceStepEditor } from '@/components/cadences/CadenceStepEditor'
import { CadenceTimeline } from '@/components/cadences/CadenceTimeline'
import { useAuth } from '@/hooks/use-auth'
import {
  useCadence,
  useCadenceSteps,
  useCreateStep,
  useDeleteStep,
  useUpdateStep,
} from '@/hooks/use-cadences'
import { useChannels } from '@/hooks/use-channels'

export const Route = createFileRoute('/_app/cadencias/$cadenceId/etapas')({
  component: CadenceStepsPage,
})

function CadenceStepsPage() {
  const { cadenceId } = Route.useParams()
  const { currentOrgId } = useAuth()
  const { data: steps = [], isLoading } = useCadenceSteps(
    currentOrgId,
    cadenceId
  )
  const { data: cadence } = useCadence(currentOrgId, cadenceId)
  const { data: allChannels = [] } = useChannels(currentOrgId)
  const createStep = useCreateStep(currentOrgId, cadenceId)
  const updateStep = useUpdateStep(currentOrgId, cadenceId)
  const deleteStep = useDeleteStep(currentOrgId, cadenceId)

  async function addStep() {
    await createStep.mutateAsync({
      position: steps.length,
      type: 'TEXT',
      content: 'Nova mensagem',
      delay_minutes: steps.length === 0 ? 0 : 60,
    })
    toast.success('Etapa adicionada')
  }

  async function move(index: number, direction: -1 | 1) {
    const current = steps[index]
    const target = steps[index + direction]
    if (!current || !target) return
    await updateStep.mutateAsync({
      stepId: current.id,
      data: { position: target.position },
    })
  }

  if (isLoading) {
    return <Skeleton className="h-80 w-full rounded-md" />
  }

  const cadenceChannels =
    cadence?.channelStrategy === 'SPECIFIC_CHANNELS'
      ? allChannels
          .filter((channel) => cadence.channel_ids.includes(channel.id))
          .map((channel) => ({ id: channel.id, name: channel.name }))
      : allChannels.map((channel) => ({ id: channel.id, name: channel.name }))

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-md border p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Sequência de etapas</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Defina as mensagens que serão enviadas ao lead, em ordem,
              respeitando o tempo de espera entre cada etapa.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => void addStep()}
            disabled={createStep.isPending}
          >
            {createStep.isPending ? <Spinner /> : <Plus className="h-4 w-4" />}
            Adicionar etapa
          </Button>
        </div>
      </section>

      {steps.length === 0 ? (
        <EmptyState
          title="Nenhuma etapa"
          description="Adicione a primeira mensagem para ativar a cadência."
          action={
            <Button onClick={() => void addStep()}>
              <Plus className="h-4 w-4" />
              Adicionar primeira etapa
            </Button>
          }
        />
      ) : (
        <>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <CadenceStepEditor
                key={step.id}
                step={step}
                index={index}
                isFirst={index === 0}
                isLast={index === steps.length - 1}
                orgId={currentOrgId}
                channels={cadenceChannels}
                onSave={async (data) => {
                  await updateStep.mutateAsync({ stepId: step.id, data })
                }}
                onDelete={async () => {
                  await deleteStep.mutateAsync(step.id)
                  toast.success('Etapa removida')
                }}
                onMoveUp={() => move(index, -1)}
                onMoveDown={() => move(index, 1)}
              />
            ))}
          </div>
          <CadenceTimeline steps={steps} channels={cadenceChannels} />
        </>
      )}
    </div>
  )
}
